/**
 * Integrity Checker - Detector de Modificación de Archivos
 * Verifica si los archivos críticos han sido modificados
 */

(function() {
  'use strict';

  // Hashes SHA-256 de los archivos críticos (generados en build)
  const FILE_HASHES = {
    'js/utils/console-guard.js': null, // Se llenará dinámicamente
    'js/user-dashboard.js': null,
    'js/home.js': null,
    // Agregar más archivos críticos aquí
  };

  /**
   * Calcula el hash SHA-256 de un contenido
   */
  async function calculateHash(content) {
    const msgBuffer = new TextEncoder().encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Verifica la integridad de un archivo
   */
  async function checkFileIntegrity(filePath, expectedHash) {
    if (!expectedHash) return { valid: null, message: 'Sin hash de referencia' };

    try {
      const response = await fetch(filePath, { 
        cache: 'no-cache',
        headers: { 'X-Integrity-Check': 'true' }
      });
      
      if (!response.ok) {
        return { valid: false, message: `Error al cargar: ${response.status}` };
      }

      const content = await response.text();
      const actualHash = await calculateHash(content);

      if (actualHash !== expectedHash) {
        return { 
          valid: false, 
          message: '⚠️ ARCHIVO MODIFICADO',
          expected: expectedHash,
          actual: actualHash
        };
      }

      return { valid: true, message: 'Integridad verificada' };

    } catch (error) {
      return { valid: false, message: `Error: ${error.message}` };
    }
  }

  /**
   * Verifica todos los archivos críticos
   */
  async function verifyAllFiles() {
    const results = {};
    let hasModifications = false;

    for (const [file, hash] of Object.entries(FILE_HASHES)) {
      if (!hash) continue; // Saltar si no hay hash

      const result = await checkFileIntegrity(file, hash);
      results[file] = result;

      if (result.valid === false) {
        hasModifications = true;
        console.error(`🚨 ALERTA DE SEGURIDAD: ${file}`, result);
      }
    }

    if (hasModifications) {
      // Enviar alerta al servidor
      sendSecurityAlert('file_modification', results);
    }

    return { hasModifications, results };
  }

  // Orígenes de scripts e iframes que usa el propio sitio (no generan alerta ni se eliminan)
  const TRUSTED_SCRIPT_HOSTS = [
    'cdn.publikaya.com',
    'unpkg.com',                    // Supabase y Leaflet, cargados con SRI
    'cdnjs.cloudflare.com',
    'cdn.jsdelivr.net',
    'pagead2.googlesyndication.com', // AdSense
    'googleads.g.doubleclick.net',
    'ep1.adtrafficquality.google',
    'ep2.adtrafficquality.google',
    'www.googletagmanager.com',
    'accounts.google.com'
  ];
  const TRUSTED_IFRAME_HOSTS = [
    'www.googletagmanager.com', 'www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'www.google.com',
    'googleads.g.doubleclick.net', 'tpc.googlesyndication.com', 'safeframe.googlesyndication.com', 'accounts.google.com'
  ];
  const EXTENSION_PROTOCOLS = ['chrome-extension:', 'moz-extension:', 'safari-extension:', 'safari-web-extension:', 'ms-browser-extension:'];

  function parseUrl(src) {
    try { return new URL(src, window.location.href); } catch (_) { return null; }
  }
  function isOwnOrTrusted(src, hosts) {
    const u = parseUrl(src);
    if (!u) return false;
    if (u.origin === window.location.origin) return true; // scripts propios del sitio
    if (/(^|\.)publikaya\.com$/i.test(u.hostname)) return true;
    return hosts.indexOf(u.hostname) !== -1;
  }
  function isExtension(src) {
    const u = parseUrl(src);
    return !!u && EXTENSION_PROTOCOLS.indexOf(u.protocol) !== -1;
  }

  // Evita repetir la misma alerta y limita cuántas envía cada visita
  const MAX_ALERTS_PER_PAGE = 5;
  let alertsSent = 0;
  function alreadyReported(key) {
    try {
      const k = 'pk_sec_' + key;
      if (sessionStorage.getItem(k)) return true;
      sessionStorage.setItem(k, '1');
    } catch (_) {}
    return false;
  }

  /**
   * Envía alerta de seguridad al backend (la gravedad la decide el servidor)
   */
  async function sendSecurityAlert(type, data) {
    try {
      if (alertsSent >= MAX_ALERTS_PER_PAGE) return;
      const dedupeKey = type + ':' + String((data && (data.src || data.field || data.extension)) || '').slice(0, 120);
      if (alreadyReported(dedupeKey)) return;
      alertsSent++;

      // Referencia local; el servidor recalcula la gravedad y no confía en este valor
      const severity = {
        'file_modification': 'critical',
        'unauthorized_script': 'high',
        'unauthorized_iframe': 'high',
        'injection_attempt': 'medium',
        'fetch_override': 'low',
        'browser_extension': 'low'
      }[type] || 'medium';

      const alertData = {
        type,
        severity,
        url: window.location.href,
        ip_address: null, // El backend lo agregará
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        data: data || {},
        status: 'pending'
      };

      // Solo en producción
      if (window.location.hostname.includes('publikaya.com')) {
        // Enviar a través del backend que tiene el Supabase Service Key
        await fetch('/api/v1/security/alert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(alertData)
        });
      } else {
        // En desarrollo, solo log
        console.log('🔒 [DEV] Alerta de seguridad:', alertData);
      }
    } catch (error) {
      console.error('Error enviando alerta de seguridad:', error);
    }
  }

  /**
   * Detecta intentos de inyección en formularios
   */
  function setupInjectionDetection() {
    // Patrones sospechosos
    const suspiciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe/gi,
      /eval\(/gi,
      /document\.write/gi,
      /window\.location/gi
    ];

    // Monitorear inputs (nunca se envía lo que el usuario escribe; las contraseñas se ignoran)
    document.addEventListener('input', (e) => {
      const el = e.target;
      if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
      if (el.type === 'password' || el.autocomplete === 'current-password' || el.autocomplete === 'new-password') return;
      const value = el.value;

      for (const pattern of suspiciousPatterns) {
        pattern.lastIndex = 0;
        if (pattern.test(value)) {
          sendSecurityAlert('injection_attempt', {
            field: String(el.name || el.id || '').slice(0, 60),
            pattern: pattern.toString()
          });
          break;
        }
      }
    }, true);
  }

  /**
   * Detecta manipulación del DOM
   */
  function setupDOMMonitoring() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'SCRIPT') {
            if (node.hasAttribute('data-trusted')) return;
            const src = node.src || '';
            // Scripts propios o de servicios que usa el sitio: permitidos, sin alerta
            if (src && isOwnOrTrusted(src, TRUSTED_SCRIPT_HOSTS)) return;
            // JSON-LD y otros bloques de datos no se ejecutan
            if (!src && node.type && !/javascript|module/i.test(node.type)) return;
            // Extensiones del navegador del visitante: no se pueden quitar ni son del sitio; aviso leve una vez
            if (src && isExtension(src)) {
              const u = parseUrl(src);
              sendSecurityAlert('browser_extension', { extension: u ? u.protocol + '//' + u.host : 'desconocida' });
              return;
            }
            console.error('🚨 Script no autorizado inyectado:', src || 'inline');
            node.remove();
            sendSecurityAlert('unauthorized_script', {
              src: (src || 'inline').slice(0, 300),
              content: src ? '' : String(node.textContent || '').substring(0, 200)
            });
            return;
          }

          if (node.tagName === 'IFRAME') {
            const src = node.src || '';
            if (!src || src === 'about:blank') return;
            if (node.hasAttribute('data-trusted') || isOwnOrTrusted(src, TRUSTED_IFRAME_HOSTS)) return;
            if (isExtension(src)) return;

            console.error('🚨 Iframe no autorizado inyectado:', src);
            node.remove();
            sendSecurityAlert('unauthorized_iframe', { src: src.slice(0, 300) });
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Verifica si se modificó el código en memoria
   */
  function setupRuntimeProtection() {
    // Proteger funciones críticas
    const originalFetch = window.fetch;
    const originalXHR = window.XMLHttpRequest;
    
    // Detectar si alguien sobrescribe fetch
    Object.defineProperty(window, 'fetch', {
      get: () => originalFetch,
      set: (newValue) => {
        if (newValue !== originalFetch) {
          console.error('🚨 Intento de sobrescribir window.fetch');
          sendSecurityAlert('fetch_override', {});
        }
      }
    });

    // Detectar modificación de localStorage
    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function(key, value) {
      // Detectar claves sospechosas
      if (key.includes('admin') || key.includes('token') || key.includes('secret')) {
        console.warn('⚠️ Modificación de clave sensible en localStorage:', key);
      }
      return originalSetItem.apply(this, arguments);
    };
  }

  /**
   * Inicializa todas las protecciones
   */
  function initialize() {
    // Solo en producción o si está habilitado explícitamente
    const enableProtection = 
      window.location.hostname.includes('publikaya.com') ||
      localStorage.getItem('enableSecurityProtection') === 'true';

    if (!enableProtection) return;

    console.log('🛡️ Sistema de seguridad activado');

    // Activar protecciones
    setupInjectionDetection();
    setupDOMMonitoring();
    setupRuntimeProtection();

    // Verificar integridad de archivos (opcional, puede ser pesado)
    if (localStorage.getItem('checkFileIntegrity') === 'true') {
      verifyAllFiles().then(result => {
        if (result.hasModifications) {
          console.error('🚨 ARCHIVOS MODIFICADOS DETECTADOS');
        }
      });
    }
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Exponer funciones para debugging
  window.__security = {
    verifyFiles: verifyAllFiles,
    calculateHash: calculateHash
  };

})();
